import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import classNames from "classnames";
import FAIcon from "../FAIcon/FAIcon";
import A11yHelpers from "../../../../util/A11yHelpers";
import { AlertInfo } from "../StandardAlerts/StandardAlerts";
import BootstrapBreakpointService from "../../../Domain/BootstrapBreakpoint/BootstrapBreakpoint.service";
import useBootstrapBreakpoint from "../../Hooks/useBootstrapBreakpoint";
import NumberService from "../../../Services/Shared/Number.service";
const DEFAULT_VERTICAL_PADDING = 2;
const DEFAULT_STRIPED = true;
const CHECKBOX_WIDTH = 25;
const HORIZONTAL_PADDING = 15;
const getColumnClass = (column, isHeader = false) => {
    if (!column) {
        return "";
    }
    const classes = ["aviso-col"];
    if (isHeader) {
        classes.push("font-weight-bold align-items-center py-2");
        if (column.onSort) {
            classes.push("clickable font-weight-bold text-primary");
        }
    }
    if (column.showAt) {
        classes.push(`d-${column.showAt}-flex`);
        classes.push("d-none");
    }
    if (column.align) {
        classes.push("justify-content-" + column.align);
        if (column.align === "end") {
            classes.push("text-right");
        }
    }
    return classes.join(" ");
};
const getColumnStyles = (index, allColumns, currentBreakpoint, isHeader = false, otherOptions = {}) => {
    var _a;
    const column = allColumns[index];
    if (!column) {
        return {};
    }
    if (otherOptions.wrapAt) {
        if (BootstrapBreakpointService.isBreakpointBefore(currentBreakpoint, otherOptions.wrapAt)) {
            return {};
        }
    }
    const allColumnsWidth = allColumns.reduce((sum, col) => {
        var _a;
        const isColumnShowing = !col.showAt ||
            !BootstrapBreakpointService.isBreakpointBefore(currentBreakpoint, col.showAt);
        if (!isColumnShowing) {
            return sum;
        }
        return sum + ((_a = col.size) !== null && _a !== void 0 ? _a : 1);
    }, 0);
    const columnPercentage = NumberService.roundToDigits((100 * ((_a = column.size) !== null && _a !== void 0 ? _a : 1)) / Math.max(allColumnsWidth, 1), 5);
    const checkboxShare = NumberService.roundToDigits((CHECKBOX_WIDTH + HORIZONTAL_PADDING * 2) / Math.max(allColumns.length, 1), 3);
    const percentageString = otherOptions.makeRoomForCheckbox
        ? `calc(${columnPercentage}% - ${checkboxShare}px)`
        : columnPercentage + "%";
    return {
        width: percentageString,
        flex: `0 0 ${percentageString}`,
    };
};
const ResponsiveTable = (props) => {
    var _a, _b, _c;
    const [sortedColumn, setSortedColumn] = React.useState(props.initialSortColumn);
    const [sortDirection, setSortDirection] = React.useState(props.initialSortDirection || null);
    const currentBreakpoint = useBootstrapBreakpoint();
    const checkboxIds = ((_a = props.addCheckboxes) === null || _a === void 0 ? void 0 : _a.ids) || ((_b = props.rows) === null || _b === void 0 ? void 0 : _b.map((_, i) => i.toString()));
    if (!((_c = props.rows) === null || _c === void 0 ? void 0 : _c.length)) {
        return typeof props.noDataComponent === "string" ? (_jsx(AlertInfo, { message: props.noDataComponent })) : (props.noDataComponent || _jsx(_Fragment, {}));
    }
    const verticalPadding = props.rowVerticalPadding == null
        ? DEFAULT_VERTICAL_PADDING
        : props.rowVerticalPadding;
    const striped = props.striped == null ? DEFAULT_STRIPED : props.striped;
    return (_jsxs("table", { className: classNames("aviso-table w-100", { hover: props.hover }), children: [_jsx("thead", { children: _jsxs("tr", { className: classNames("aviso-row border-bottom", {
                        [`d-none d-${props.wrapAt || ""}-flex`]: props.wrapAt,
                    }), children: [props.addCheckboxes && (_jsx("div", { style: { width: CHECKBOX_WIDTH }, className: "aviso-col", children: props.addCheckboxes.useCheckboxesMakeCheckbox(checkboxIds) })), props.columns.map((column, index) => (_jsxs("th", Object.assign({ role: column.onSort ? "button" : undefined, style: getColumnStyles(index, props.columns, currentBreakpoint, true, {
                                wrapAt: props.wrapAt,
                                makeRoomForCheckbox: !!props.addCheckboxes,
                            }), className: getColumnClass(column, true) }, A11yHelpers.onClickOrEnterOrSpace(() => {
                            if (column.onSort) {
                                let direction = "asc";
                                if (sortedColumn === index) {
                                    if (sortDirection === "asc") {
                                        direction = "desc";
                                    }
                                    else if (sortDirection === "desc") {
                                        direction = null;
                                    }
                                }
                                setSortedColumn(index);
                                setSortDirection(direction);
                                // Check if onSort exists again for flow's sake.
                                if (column.onSort) {
                                    column.onSort(direction);
                                }
                            }
                        }), { scope: "col", children: [column.title, index === sortedColumn && sortDirection && (_jsx(FAIcon, { icon: sortDirection === "asc" ? "chevron-down" : "chevron-up", className: "ml-2" }))] }), index)))] }) }), _jsx("tbody", { children: props.rows.map((row, index) => (_jsxs("tr", Object.assign({ className: classNames("row mx-0 border-bottom aviso-row", {
                        clickable: props.onRowClick,
                        "bg-stripe": striped && index % 2 === 0,
                        [`py-${verticalPadding || ""}`]: verticalPadding,
                    }) }, A11yHelpers.onClickOrEnterOrSpace(() => props.onRowClick && props.onRowClick(index)), { children: [props.addCheckboxes && (_jsx("div", { style: { width: CHECKBOX_WIDTH }, className: "aviso-col align-items-bottom", children: props.addCheckboxes.useCheckboxesMakeCheckbox(checkboxIds[index]) })), row.slice(0, props.columns.length).map((cell, cellIndex) => (_jsx("td", { className: getColumnClass(props.columns[cellIndex]), style: getColumnStyles(cellIndex, props.columns, currentBreakpoint, false, {
                                wrapAt: props.wrapAt,
                                makeRoomForCheckbox: !!props.addCheckboxes,
                            }), children: cell }, cellIndex)))] }), index))) })] }));
};
export default ResponsiveTable;
